import React, { useState } from 'react';
import { COLORS } from '../../../../utils/constants';
import { StatCard } from './TeamUIComponents';

const StatsView = ({ managerStats, adminStats, distributionStats, isSuperAdmin }) => {
  const [statsTab, setStatsTab] = useState(isSuperAdmin ? 'admins' : 'managers');
  
  const statusLabels = {
    'NEW': 'Новый',
    'IN_WORK': 'В работе',
    'LOUD': 'Loud',
    'OPEN': 'Open',
    'WITHDRAWAL': 'Вывод',
    'RESET': 'Reset',
    'LOUD2': 'Loud2'
  };

  const statusColors = {
    'NEW': '#3b82f6',
    'IN_WORK': '#f59e0b',
    'LOUD': '#8b5cf6',
    'OPEN': '#22c55e',
    'WITHDRAWAL': COLORS.danger,
    'RESET': '#6b7280',
    'LOUD2': '#a855f7'
  };

  const allStatuses = ['NEW', 'IN_WORK', 'LOUD', 'OPEN', 'WITHDRAWAL', 'LOUD2', 'RESET'];

  const adminDistribution = distributionStats?.admin_distribution || [];
  const managerDistribution = distributionStats?.manager_distribution || [];

  const calculateStatusTotals = (items) => {
    const totals = {};
    items.forEach(item => {
      Object.entries(item.status_distribution || {}).forEach(([status, count]) => {
        totals[status] = (totals[status] || 0) + count;
      });
    });
    return totals;
  };

  const calculateTagTotals = (items) => {
    const totals = {};
    items.forEach(item => {
      Object.entries(item.tag_distribution || {}).forEach(([tag, count]) => {
        totals[tag] = (totals[tag] || 0) + count;
      });
    });
    return totals;
  };

  const managerTotals = managerStats.reduce((acc, stat) => ({
    clients: acc.clients + (stat.client_count || 0),
    activeClients: acc.activeClients + (stat.active_clients || 0),
    totalBalance: acc.totalBalance + (stat.total_balance || 0),
    totalDeposits: acc.totalDeposits + (stat.total_deposits || 0),
    totalWithdrawals: acc.totalWithdrawals + (stat.total_withdrawals || 0),
    openPositions: acc.openPositions + (stat.open_positions || 0)
  }), { clients: 0, activeClients: 0, totalBalance: 0, totalDeposits: 0, totalWithdrawals: 0, openPositions: 0 });

  const adminTotals = adminStats.reduce((acc, stat) => ({
    managers: acc.managers + (stat.manager_count || 0),
    clients: acc.clients + (stat.client_count || 0),
    activeClients: acc.activeClients + (stat.active_clients || 0),
    newClientsMonth: acc.newClientsMonth + (stat.new_clients_month || 0),
    totalBalance: acc.totalBalance + (stat.total_balance || 0),
    totalDeposits: acc.totalDeposits + (stat.total_deposits || 0),
    totalWithdrawals: acc.totalWithdrawals + (stat.total_withdrawals || 0),
    pendingDeposits: acc.pendingDeposits + (stat.pending_deposits || 0),
    pendingWithdrawals: acc.pendingWithdrawals + (stat.pending_withdrawals || 0),
    netIncome: acc.netIncome + (stat.net_income || 0)
  }), { managers: 0, clients: 0, activeClients: 0, newClientsMonth: 0, totalBalance: 0, totalDeposits: 0, totalWithdrawals: 0, pendingDeposits: 0, pendingWithdrawals: 0, netIncome: 0 });

  const totals = statsTab === 'admins' ? adminTotals : managerTotals;

  const renderDistributionTable = (items, title, icon, headerColor, showManagerCount = false) => {
    if (!items || items.length === 0) return null;
    
    const statusTotals = calculateStatusTotals(items);
    const tagTotals = calculateTagTotals(items);
    const totalClients = items.reduce((sum, i) => sum + (i.total_clients || 0), 0);
    
    return (
      <div style={{ backgroundColor: COLORS.card, borderRadius: '12px', border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', backgroundColor: `${headerColor}10`, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: headerColor, fontSize: '14px', fontWeight: '600' }}>{icon} {title}</span>
          <span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>Всего: {totalClients} клиентов</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: COLORS.background }}>
                <th style={{ padding: '12px', textAlign: 'left', color: COLORS.textSecondary, fontSize: '11px', fontWeight: '600' }}>
                  {showManagerCount ? 'АДМИНИСТРАТОР' : 'МЕНЕДЖЕР'}
                </th>
                {showManagerCount && (
                  <th style={{ padding: '12px', textAlign: 'center', color: COLORS.textSecondary, fontSize: '11px', fontWeight: '600' }}>👥</th>
                )}
                <th style={{ padding: '12px', textAlign: 'center', color: COLORS.textSecondary, fontSize: '11px', fontWeight: '600' }}>ВСЕГО</th>
                {allStatuses.map(status => (
                  <th key={status} style={{ padding: '12px', textAlign: 'center', color: statusColors[status] || COLORS.textSecondary, fontSize: '11px', fontWeight: '600' }}>
                    {status}
                  </th>
                ))}
                <th style={{ padding: '12px', textAlign: 'left', color: COLORS.textSecondary, fontSize: '11px', fontWeight: '600' }}>ТЕГИ</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} style={{ borderBottom: `1px solid ${COLORS.border}20` }}>
                  <td style={{ padding: '12px' }}>
                    <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '500' }}>
                      {item.name}
                      {item.parent_admin && (
                        <span style={{ 
                          marginLeft: '8px',
                          padding: '2px 6px',
                          backgroundColor: `${COLORS.warning}20`,
                          color: COLORS.warning,
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: '600'
                        }}>→ {item.parent_admin.name || item.parent_admin.email}</span>
                      )}
                    </div>
                    <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>
                      {item.email}
                      {item.subordinate_admins_count > 0 && (
                        <span style={{ marginLeft: '8px', color: '#8b5cf6' }}>
                          • {item.subordinate_admins_count} подч. админов
                        </span>
                      )}
                    </div>
                  </td>
                  {showManagerCount && (
                    <td style={{ padding: '12px', textAlign: 'center', color: '#3b82f6', fontWeight: '600', fontSize: '14px' }}>
                      {item.manager_count || 0}
                    </td>
                  )}
                  <td style={{ padding: '12px', textAlign: 'center', color: COLORS.text, fontSize: '16px', fontWeight: '700' }}>
                    {item.total_clients}
                  </td>
                  {allStatuses.map(status => (
                    <td key={status} style={{ padding: '12px', textAlign: 'center', color: statusColors[status], fontWeight: '600' }}>
                      {item.status_distribution?.[status] || 0}
                    </td>
                  ))}
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {Object.entries(item.tag_distribution || {}).slice(0, 3).map(([tag, count]) => (
                        <span key={tag} style={{ padding: '2px 6px', backgroundColor: COLORS.background, borderRadius: '4px', fontSize: '10px', color: COLORS.text }}>{tag}: {count}</span>
                      ))}
                      {Object.keys(item.tag_distribution || {}).length > 3 && (
                        <span style={{ color: COLORS.textSecondary, fontSize: '10px' }}>+{Object.keys(item.tag_distribution).length - 3}</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              <tr style={{ backgroundColor: COLORS.background, borderTop: `2px solid ${COLORS.border}` }}>
                <td style={{ padding: '12px', fontWeight: '700', color: COLORS.text }}>ИТОГО</td>
                {showManagerCount && <td style={{ padding: '12px', textAlign: 'center', fontWeight: '700', color: '#3b82f6' }}>—</td>}
                <td style={{ padding: '12px', textAlign: 'center', fontWeight: '700', color: COLORS.text, fontSize: '16px' }}>{totalClients}</td>
                {allStatuses.map(status => (
                  <td key={status} style={{ padding: '12px', textAlign: 'center', fontWeight: '700', color: statusColors[status] }}>
                    {statusTotals[status] || 0}
                  </td>
                ))}
                <td style={{ padding: '12px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {Object.entries(tagTotals).slice(0, 4).map(([tag, count]) => (
                      <span key={tag} style={{ padding: '2px 6px', backgroundColor: `${headerColor}15`, borderRadius: '4px', fontSize: '10px', color: headerColor, fontWeight: '600' }}>{tag}: {count}</span>
                    ))}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'grid', gap: '20px' }}>
      {isSuperAdmin && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button onClick={() => setStatsTab('admins')} style={{
            padding: '8px 16px', backgroundColor: statsTab === 'admins' ? '#8b5cf6' : COLORS.card,
            border: `1px solid ${statsTab === 'admins' ? '#8b5cf6' : COLORS.border}`, borderRadius: '6px',
            color: statsTab === 'admins' ? '#fff' : COLORS.textSecondary, fontWeight: '600', fontSize: '13px', cursor: 'pointer'
          }}>По администраторам</button>
          <button onClick={() => setStatsTab('managers')} style={{
            padding: '8px 16px', backgroundColor: statsTab === 'managers' ? '#3b82f6' : COLORS.card,
            border: `1px solid ${statsTab === 'managers' ? '#3b82f6' : COLORS.border}`, borderRadius: '6px',
            color: statsTab === 'managers' ? '#fff' : COLORS.textSecondary, fontWeight: '600', fontSize: '13px', cursor: 'pointer'
          }}>По менеджерам</button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
        {statsTab === 'admins' && <StatCard label="Менеджеров" value={totals.managers} color="#3b82f6" />}
        <StatCard label="Всего клиентов" value={totals.clients} color={COLORS.text} subtext={`${totals.activeClients} активных`} />
        {statsTab === 'admins' && <StatCard label="Новых за месяц" value={totals.newClientsMonth} color="#8b5cf6" />}
        <StatCard label="Общий баланс" value={`$${totals.totalBalance.toFixed(2)}`} color={COLORS.primary} />
        <StatCard label="Депозиты" value={`$${totals.totalDeposits.toFixed(2)}`} color={COLORS.success} />
        <StatCard label="Выводы" value={`$${totals.totalWithdrawals.toFixed(2)}`} color={COLORS.danger} />
        {statsTab === 'admins' && <StatCard label="Pending" value={totals.pendingDeposits + totals.pendingWithdrawals} color="#f59e0b" />}
        <StatCard label="Чистый доход" value={`$${(totals.totalDeposits - totals.totalWithdrawals).toFixed(2)}`} 
          color={(totals.totalDeposits - totals.totalWithdrawals) >= 0 ? COLORS.success : COLORS.danger} />
      </div>

      {statsTab === 'admins' && isSuperAdmin && (
        renderDistributionTable(adminDistribution, 'Администраторы (включая клиентов менеджеров)', '👔', '#8b5cf6', true)
      )}

      {statsTab === 'managers' && (
        renderDistributionTable(managerDistribution, 'Менеджеры', '👥', '#3b82f6', false)
      )}
    </div>
  );
};

export default StatsView;
